import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiUrl } from '../lib/api';

const AffiliateApplyPage = () => {
  const navigate = useNavigate();
  const MONO = "'JetBrains Mono', monospace";
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState({});
  const [roleOther, setRoleOther] = useState(false);
  const [skillOther, setSkillOther] = useState(false);
  const [channelsOther, setChannelsOther] = useState(false);

  const [form, setForm] = useState({
    full_name: "", email: "", profile_link: "", role: "", role_state: "",
    skill: "", skill_state: "", what_you_do: "", audience: "", reach: "",
    reach_state: "", acquisition_plan: "", channels: [], channels_state: "",
    past_promos: "", compliance: "", compliance_state: "", terms_agree: false,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleChannel = (v) => {
    const current = form.channels;
    if (current.includes(v)) set("channels", current.filter(c => c !== v));
    else set("channels", [...current, v]);
    if (v === "Other") setChannelsOther(!current.includes(v));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name) e.full_name = "This field is required.";
    if (!form.email || !form.email.includes("@")) e.email = "A valid email address is required.";
    if (!form.profile_link) e.profile_link = "This field is required.";
    if (!form.role) e.role = "Please select an option.";
    if (form.role === "Other" && !form.role_state) e.role_state = "Please state your role.";
    if (!form.skill) e.skill = "Please select an option.";
    if (form.skill === "Other" && !form.skill_state) e.skill_state = "Please state your skill.";
    if (!form.what_you_do) e.what_you_do = "This field is required.";
    if (!form.audience) e.audience = "This field is required.";
    if (!form.reach) e.reach = "Please select an option.";
    if (!form.acquisition_plan) e.acquisition_plan = "This field is required.";
    if (form.channels.length === 0) e.channels = "Please select at least one channel.";
    if (form.channels.includes("Other") && !form.channels_state) e.channels_state = "Please state your channel.";
    if (!form.past_promos) e.past_promos = "This field is required.";
    if (!form.compliance) e.compliance = "Please select an option.";
    if (!form.terms_agree) e.terms_agree = "You must agree to the Affiliate Program Terms to apply.";
    return e;
  };

  const submit = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setLoading(true);
    setSubmitError('');
    try {
      const res = await fetch(apiUrl('/api/affiliates/apply'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.full_name,
          email: form.email,
          profile_link: form.profile_link,
          role: form.role,
          role_state: form.role_state,
          skill: form.skill,
          skill_state: form.skill_state,
          what_you_do: form.what_you_do,
          audience: form.audience,
          reach: form.reach,
          reach_state: form.reach_state,
          acquisition_plan: form.acquisition_plan,
          channels: form.channels,
          channels_state: form.channels_state,
          past_promos: form.past_promos,
          compliance: form.compliance,
          compliance_state: form.compliance_state
        })
      });
      const data = await res.json();
      if (res.status === 409) {
        setSubmitError('An application with this email already exists. Status: ' + (data.status || 'pending review'));
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (!res.ok) {
        setSubmitError(data.error || 'Something went wrong. Please try again.');
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitError('Network error. Please check your connection and try again.');
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: "100%", padding: "9px 12px", fontSize: "13px", border: "0.5px solid #1a3050", borderRadius: "7px", background: "#0a1220", color: "#c8d4e0", fontFamily: MONO, outline: "none" };
  const labelStyle = { display: "block", fontSize: "13px", fontWeight: "600", color: "#8090b0", marginBottom: "5px", fontFamily: MONO };
  const helpStyle = { fontSize: "11px", color: "#2a4060", marginBottom: "6px", lineHeight: "1.5", fontFamily: MONO };
  const errStyle = { fontSize: "11px", color: "#e24b4a", marginTop: "4px", fontFamily: MONO };
  const sectionLabel = { fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#1a3050", fontWeight: "600", fontFamily: MONO, marginBottom: "20px" };
  const optStyle = { display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px", cursor: "pointer" };
  const optTextStyle = { fontSize: "13px", color: "#6080a0", fontFamily: MONO };
  const pleaseStateLabel = (required) => ({ display: "block", fontSize: "11px", color: "#2a4060", marginBottom: "4px", fontFamily: MONO, marginTop: "10px" });
  const pleaseStateInput = { width: "100%", padding: "7px 10px", fontSize: "12px", border: "0.5px solid #0d1e35", borderRadius: "6px", background: "#080c18", color: "#4a6070", fontFamily: MONO, outline: "none" };

  const roles = ["Agency owner", "Consultant / coach", "Creator / educator", "Freelancer", "Community owner", "Sales / business development", "Media / content operator", "Other"];
  const skills = ["Sales", "Content creation", "Audience building", "Outreach / lead generation", "Partnerships", "Community building", "Paid ads", "Operations / systems", "Other"];
  const reaches = ["Under 50", "50–200", "200–1,000", "1,000–5,000", "5,000+"];
  const channels = ["Warm introductions", "Personal referrals", "Email list", "LinkedIn posts", "Short-form content", "YouTube demos", "Private communities", "Outreach / DMs", "Paid ads", "Blog / SEO", "Other"];

  const HR = () => <hr style={{ border: "none", borderTop: "1px solid #0d1e35", margin: "36px 0 28px" }} />;

  if (submitted) return (
    <div style={{ minHeight: "100vh", background: "#080c18", fontFamily: MONO }}>
      <div style={{ borderBottom: "1px solid #0d1e35", padding: "14px 32px" }}>
        <div style={{ maxWidth: "640px", margin: "0 auto" }}>
          <a href="/affiliate-program" style={{ color: "#00e5cc", fontSize: "12px", textDecoration: "none" }}>← Back to Affiliate Program</a>
        </div>
      </div>
      <div style={{ maxWidth: "640px", margin: "0 auto", padding: "80px 32px", textAlign: "center" }}>
        <div style={{ width: "48px", height: "48px", border: "1px solid #00e5cc33", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: "20px", color: "#00e5cc" }}>✓</div>
        <h1 style={{ fontSize: "22px", fontWeight: "700", color: "#ffffff", marginBottom: "12px" }}>Application Submitted</h1>
        <p style={{ fontSize: "13px", color: "#3a5a7a", lineHeight: "1.8", marginBottom: "32px" }}>We have received your application and will review it manually. You will hear back within 10 business days.</p>
        <a href="/" style={{ display: "inline-block", padding: "11px 28px", background: "#1a2a4a", color: "#c8d4e0", border: "1px solid #00e5cc", borderRadius: "7px", fontSize: "12px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", textDecoration: "none", fontFamily: MONO }}>Return to Base</a>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#080c18", color: "#c8d4e0", fontFamily: MONO }}>
      <div style={{ borderBottom: "1px solid #0d1e35", padding: "14px 32px" }}>
        <div style={{ maxWidth: "640px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <a href="/affiliate-program" style={{ color: "#00e5cc", fontSize: "12px", textDecoration: "none" }}>← Affiliate Program</a>
          <a href="/" style={{ color: "#2a4060", fontSize: "11px", textDecoration: "none", letterSpacing: "0.06em", textTransform: "uppercase" }}>RecallCore</a>
        </div>
      </div>

      <div style={{ maxWidth: "640px", margin: "0 auto", padding: "52px 32px 100px" }}>
        <p style={{ fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", color: "#1a3a5a", marginBottom: "10px", fontWeight: 600 }}>RecallCore</p>
        <h1 style={{ fontSize: "26px", fontWeight: "700", color: "#ffffff", letterSpacing: "-0.02em", marginBottom: "8px" }}>Affiliate Program Application</h1>
        <p style={{ fontSize: "12px", color: "#2a4060", marginBottom: "36px" }}>All fields marked <span style={{ color: "#e24b4a" }}>*</span> are required. Applications are reviewed manually.</p>

        {Object.keys(errors).length > 0 && (
          <div style={{ padding: "14px 16px", background: "#1a0808", border: "1px solid #e24b4a44", borderRadius: "8px", marginBottom: "28px" }}>
            <p style={{ fontSize: "12px", color: "#e24b4a", fontFamily: MONO }}>Please fix the errors below before submitting.</p>
          </div>
        )}

        <p style={sectionLabel}>Section 1 — Identity</p>

        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>Full name <span style={{ color: "#e24b4a" }}>*</span></label>
          <input style={inputStyle} type="text" value={form.full_name} onChange={e => set("full_name", e.target.value)} placeholder="Your full name" />
          {errors.full_name && <p style={errStyle}>{errors.full_name}</p>}
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>Email address <span style={{ color: "#e24b4a" }}>*</span></label>
          <input style={inputStyle} type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@example.com" />
          {errors.email && <p style={errStyle}>{errors.email}</p>}
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>Website or primary profile link <span style={{ color: "#e24b4a" }}>*</span></label>
          <p style={helpStyle}>Main website, LinkedIn, X, Instagram, YouTube, or community page</p>
          <input style={inputStyle} type="url" value={form.profile_link} onChange={e => set("profile_link", e.target.value)} placeholder="https://" />
          {errors.profile_link && <p style={errStyle}>{errors.profile_link}</p>}
        </div>

        <HR />
        <p style={sectionLabel}>Section 2 — Role and Positioning</p>

        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>What best describes your current role? <span style={{ color: "#e24b4a" }}>*</span></label>
          {roles.map(r => (
            <label key={r} style={optStyle}>
              <input type="radio" name="role" value={r} checked={form.role === r} onChange={() => { set("role", r); setRoleOther(r === "Other"); }} style={{ accentColor: "#00e5cc", cursor: "pointer" }} />
              <span style={optTextStyle}>{r}</span>
            </label>
          ))}
          <label style={pleaseStateLabel(roleOther)}>{roleOther ? "Please state (required)" : "Please state (optional)"} {roleOther && <span style={{ color: "#e24b4a" }}>*</span>}</label>
          <input style={pleaseStateInput} type="text" value={form.role_state} onChange={e => set("role_state", e.target.value)} />
          {errors.role && <p style={errStyle}>{errors.role}</p>}
          {errors.role_state && <p style={errStyle}>{errors.role_state}</p>}
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>What is your dominant skill or strongest position? <span style={{ color: "#e24b4a" }}>*</span></label>
          {skills.map(s => (
            <label key={s} style={optStyle}>
              <input type="radio" name="skill" value={s} checked={form.skill === s} onChange={() => { set("skill", s); setSkillOther(s === "Other"); }} style={{ accentColor: "#00e5cc", cursor: "pointer" }} />
              <span style={optTextStyle}>{s}</span>
            </label>
          ))}
          <label style={pleaseStateLabel(skillOther)}>{skillOther ? "Please state (required)" : "Please state (optional)"} {skillOther && <span style={{ color: "#e24b4a" }}>*</span>}</label>
          <input style={pleaseStateInput} type="text" value={form.skill_state} onChange={e => set("skill_state", e.target.value)} />
          {errors.skill && <p style={errStyle}>{errors.skill}</p>}
          {errors.skill_state && <p style={errStyle}>{errors.skill_state}</p>}
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>Briefly describe what you do and who you help. <span style={{ color: "#e24b4a" }}>*</span></label>
          <textarea style={{ ...inputStyle, minHeight: "100px", resize: "vertical" }} value={form.what_you_do} onChange={e => set("what_you_do", e.target.value)} placeholder="Describe your work and the people you serve..." />
          {errors.what_you_do && <p style={errStyle}>{errors.what_you_do}</p>}
        </div>

        <HR />
        <p style={sectionLabel}>Section 3 — Audience and Access</p>

        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>Who are your real connections or audiences that fit RecallCore? <span style={{ color: "#e24b4a" }}>*</span></label>
          <p style={helpStyle}>Tell us who you have direct access to. Example: founders, agency owners, consultants, creators, local business operators, startup teams, ecommerce brands, sales teams, or private communities.</p>
          <textarea style={{ ...inputStyle, minHeight: "100px", resize: "vertical" }} value={form.audience} onChange={e => set("audience", e.target.value)} placeholder="Describe your audience and connections..." />
          {errors.audience && <p style={errStyle}>{errors.audience}</p>}
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>How many relevant people can you realistically reach? <span style={{ color: "#e24b4a" }}>*</span></label>
          {reaches.map(r => (
            <label key={r} style={optStyle}>
              <input type="radio" name="reach" value={r} checked={form.reach === r} onChange={() => set("reach", r)} style={{ accentColor: "#00e5cc", cursor: "pointer" }} />
              <span style={optTextStyle}>{r}</span>
            </label>
          ))}
          <label style={pleaseStateLabel(false)}>Please state (optional)</label>
          <input style={pleaseStateInput} type="text" value={form.reach_state} onChange={e => set("reach_state", e.target.value)} placeholder="Add context if helpful" />
          {errors.reach && <p style={errStyle}>{errors.reach}</p>}
        </div>

        <HR />
        <p style={sectionLabel}>Section 4 — Client Acquisition Plan</p>

        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>If approved, how will you get clients? <span style={{ color: "#e24b4a" }}>*</span></label>
          <p style={helpStyle}>Be specific. Tell us exactly how you would bring in qualified customers — through content, warm referrals, private network, outreach, demos, consulting clients, email list, communities, paid ads, or other channels.</p>
          <textarea style={{ ...inputStyle, minHeight: "110px", resize: "vertical" }} value={form.acquisition_plan} onChange={e => set("acquisition_plan", e.target.value)} placeholder="Describe your approach in detail..." />
          {errors.acquisition_plan && <p style={errStyle}>{errors.acquisition_plan}</p>}
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>Which channels will you use first? <span style={{ color: "#e24b4a" }}>*</span></label>
          {channels.map(c => (
            <label key={c} style={optStyle}>
              <input type="checkbox" value={c} checked={form.channels.includes(c)} onChange={() => toggleChannel(c)} style={{ accentColor: "#00e5cc", cursor: "pointer", width: "16px", height: "16px" }} />
              <span style={optTextStyle}>{c}</span>
            </label>
          ))}
          <label style={pleaseStateLabel(channelsOther)}>{channelsOther ? "Please state (required)" : "Please state (optional)"} {channelsOther && <span style={{ color: "#e24b4a" }}>*</span>}</label>
          <input style={pleaseStateInput} type="text" value={form.channels_state} onChange={e => set("channels_state", e.target.value)} />
          {errors.channels && <p style={errStyle}>{errors.channels}</p>}
          {errors.channels_state && <p style={errStyle}>{errors.channels_state}</p>}
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>Have you promoted SaaS, software, subscriptions, or digital services before? <span style={{ color: "#e24b4a" }}>*</span></label>
          <textarea style={{ ...inputStyle, minHeight: "100px", resize: "vertical" }} value={form.past_promos} onChange={e => set("past_promos", e.target.value)} placeholder="Describe any past experience promoting digital products or services..." />
          {errors.past_promos && <p style={errStyle}>{errors.past_promos}</p>}
        </div>

        <HR />
        <p style={sectionLabel}>Section 5 — Compliance</p>

        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>Do you agree to clearly disclose affiliate relationships and avoid spam, fake reviews, misleading claims, trademark bidding, or deceptive promotion? <span style={{ color: "#e24b4a" }}>*</span></label>
          {["Yes", "No"].map(v => (
            <label key={v} style={optStyle}>
              <input type="radio" name="compliance" value={v} checked={form.compliance === v} onChange={() => set("compliance", v)} style={{ accentColor: "#00e5cc", cursor: "pointer" }} />
              <span style={optTextStyle}>{v}</span>
            </label>
          ))}
          <label style={pleaseStateLabel(false)}>Please state (optional)</label>
          <input style={pleaseStateInput} type="text" value={form.compliance_state} onChange={e => set("compliance_state", e.target.value)} placeholder="Add any notes" />
          {errors.compliance && <p style={errStyle}>{errors.compliance}</p>}
        </div>

        <HR />

        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer" }}>
            <input type="checkbox" checked={form.terms_agree} onChange={e => set("terms_agree", e.target.checked)} style={{ accentColor: "#00e5cc", cursor: "pointer", width: "16px", height: "16px", marginTop: "2px", flexShrink: 0 }} />
            <span style={{ fontSize: "13px", color: "#6080a0", lineHeight: "1.6", fontFamily: MONO }}>
              I have read and agree to the <a href="/affiliate-program" style={{ color: "#00e5cc", textDecoration: "none" }}>Affiliate Program Terms</a>. <span style={{ color: "#e24b4a" }}>*</span>
            </span>
          </label>
          {errors.terms_agree && <p style={{ ...errStyle, marginLeft: "26px" }}>{errors.terms_agree}</p>}
        </div>

        <p style={{ fontSize: "11px", color: "#1a3050", lineHeight: "1.7", marginBottom: "24px", fontFamily: MONO }}>RecallCore may reject, suspend, terminate, withhold, reverse, or forfeit commissions for fraud, unlawful conduct, deceptive promotion, policy violations, or other misconduct.</p>

        {submitError && (
          <div style={{ padding: "12px 16px", background: "#1a0808", border: "1px solid #e24b4a44", borderRadius: "8px", marginBottom: "16px" }}>
            <p style={{ fontSize: "12px", color: "#e24b4a", fontFamily: MONO, margin: 0 }}>{submitError}</p>
          </div>
        )}
        <button onClick={submit} disabled={loading} style={{ width: "100%", padding: "13px", fontSize: "13px", fontWeight: "700", background: loading ? "#0f1a2e" : "#1a2a4a", color: loading ? "#2a4060" : "#c8d4e0", border: "1px solid #00e5cc", borderRadius: "7px", cursor: loading ? "not-allowed" : "pointer", letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: MONO }}>
          {loading ? "Submitting..." : "Apply to Affiliate Program"}
        </button>
        <p style={{ fontSize: "11px", color: "#1a3050", textAlign: "center", marginTop: "10px", fontFamily: MONO }}>Applications are reviewed manually.</p>
      </div>

      <div style={{ borderTop: "1px solid #0a1a30", padding: "18px 32px", textAlign: "center" }}>
        <p style={{ fontSize: "11px", color: "#1a2e48", margin: 0, fontFamily: MONO }}>
          © 2026 RecallCore · Codex Multiverse Limited &nbsp;·&nbsp;
          <a href="/privacy" style={{ color: "#1a3050", textDecoration: "none" }}>Privacy</a>&nbsp;·&nbsp;
          <a href="/terms" style={{ color: "#1a3050", textDecoration: "none" }}>Terms</a>&nbsp;·&nbsp;
          <a href="/contact" style={{ color: "#1a3050", textDecoration: "none" }}>Contact</a>
        </p>
      </div>
    </div>
  );
};

export default AffiliateApplyPage;
